import React, { useEffect, useState, useMemo } from "react";
import {
  Box,
  Paper,
  Typography,
  Grid,
  Divider,
  Chip,
  Card,
  CardContent,
  Stack,
  Button,
  Collapse,
  TableContainer,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Avatar,
  Container,
} from "@mui/material";
import {
  Hotel as HotelIcon,
  CalendarMonth as CalendarIcon,
  Payment as PaymentIcon,
  Person as PersonIcon,
  Download as DownloadIcon,
  Print as PrintIcon,
  ArrowBack as ArrowBackIcon,
  ReceiptLong as ReceiptLongIcon,
  LocationOn as LocationOnIcon,
} from "@mui/icons-material";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import dayjs from "dayjs";
import { searchAPI } from "../API/Seach";
import Loader from "../Components/Loader";
import { tokens } from "../theme/tokens";
import { formatBookingLocation, formatPaymentStatusDisplay } from "../shared/bookingHistoryShared";

/** Same outer layout as Guest detail / Review booking — gradient margins, centered white column */
const PAGE_SHELL_BOX_SX = {
  minHeight: "100vh",
  /* Clear fixed AppBar + gap (same idea as booking history / transaction list) */
  pt: { xs: "88px", md: "78px" },
  pb: 4,
  bgcolor: "#f0f4f8",
  backgroundImage:
    "linear-gradient(180deg, #f0f4f8 0%, #ffffff 420px, #f5f7fa 100%)",
};

const PAGE_SHELL_CONTAINER_SX = {
  position: "relative",
  px: { xs: 2, sm: 3, md: 4 },
  py: 3,
  bgcolor: "white",
  borderRadius: 2,
  boxShadow: "0 0 24px rgba(0,0,0,0.06)",
  overflow: "visible",
};

function formatMoney(amount, currency = "INR") {
  if (amount == null || Number.isNaN(Number(amount))) return "—";
  const n = Number(amount);
  if (currency === "INR") return `₹${n.toLocaleString("en-IN")}`;
  return `${currency} ${n.toLocaleString("en-IN")}`;
}

function formatDateTime(value) {
  if (!value) return "—";
  try {
    return dayjs(value).format("DD MMM YYYY, hh:mm A");
  } catch {
    return String(value);
  }
}

function formatDate(value) {
  if (!value) return "—";
  try {
    return dayjs(value).format("DD MMM YYYY");
  } catch {
    return String(value);
  }
}

function statusChipColor(status) {
  const u = String(status || "").toUpperCase();
  if (u.includes("CANCEL")) return "error";
  if (u.includes("PENDING") || u.includes("PROGRESS") || u === "NEW" || u === "INITIATED")
    return "warning";
  if (u.includes("CONFIRM") || u.includes("CHARGED") || u.includes("SUCCESS") || u === "PAID")
    return "success";
  return "default";
}

function parseCancellationPolicies(raw) {
  if (!raw || typeof raw !== "string") return [];
  const s = raw.trim();
  if (s === "" || s === "null") return [];
  try {
    const arr = JSON.parse(s);
    const rules = [];
    for (const block of Array.isArray(arr) ? arr : []) {
      for (const r of block?.rules || []) {
        rules.push(r);
      }
    }
    return rules;
  } catch {
    return [];
  }
}

function displayText(v) {
  if (v == null) return "—";
  const t = String(v).trim();
  if (t === "" || t === "null") return "—";
  return t;
}

function DetailRow({ label, value, monospace }) {
  return (
    <Stack direction="row" justifyContent="space-between" alignItems="flex-start" gap={2} sx={{ py: 0.75 }}>
      <Typography variant="caption" color="text.secondary" sx={{ flexShrink: 0, fontWeight: 600 }}>
        {label}
      </Typography>
      <Typography
        variant="body2"
        fontWeight={600}
        textAlign="right"
        sx={{
          wordBreak: "break-word",
          fontFamily: monospace ? "ui-monospace, monospace" : "inherit",
        }}
      >
        {value ?? "—"}
      </Typography>
    </Stack>
  );
}

function SectionCard({ icon, title, children, sx }) {
  return (
    <Card
      elevation={0}
      sx={{
        height: "100%",
        border: `1px solid ${tokens.border}`,
        borderRadius: `${tokens.radiusMd}px`,
        boxShadow: tokens.shadowSm,
        ...sx,
      }}
    >
      <CardContent sx={{ p: 2.5 }}>
        <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 2 }}>
          <Avatar
            sx={{
              width: 36,
              height: 36,
              bgcolor: tokens.accentSoft,
              color: tokens.accent,
            }}
          >
            {icon}
          </Avatar>
          <Typography variant="h6" sx={{ fontFamily: tokens.fontDisplay, fontSize: "1.05rem" }}>
            {title}
          </Typography>
        </Stack>
        {children}
      </CardContent>
    </Card>
  );
}

function CompleteBookingDetailsView({ data, onBack }) {
  const { order, booking, guests } = data;

  const cancelRules = useMemo(
    () => parseCancellationPolicies(booking?.cancellationPolicy),
    [booking?.cancellationPolicy]
  );

  return (
    <>
      <ToastContainer position="top-right" autoClose={3000} />
      <Box sx={PAGE_SHELL_BOX_SX}>
        <Container maxWidth="xl" sx={PAGE_SHELL_CONTAINER_SX}>
          <Paper
            elevation={0}
            sx={{
              width: "100%",
              p: { xs: 2, md: 3.5 },
              borderRadius: `${tokens.radiusLg}px`,
              border: `1px solid ${tokens.border}`,
              boxShadow: "none",
              bgcolor: tokens.paper,
            }}
          >
        <Stack direction={{ xs: "column", sm: "row" }} spacing={2} justifyContent="space-between" alignItems={{ xs: "stretch", sm: "flex-start" }} sx={{ mb: 3 }}>
          <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap">
            <Button
              startIcon={<ArrowBackIcon />}
              onClick={onBack}
              variant="text"
              color="primary"
              sx={{ alignSelf: "flex-start", minWidth: 0, px: 1 }}
            >
              Back
            </Button>
            <Box>
              <Typography
                variant="h4"
                sx={{
                  fontFamily: tokens.fontDisplay,
                  fontWeight: 700,
                  color: tokens.brand,
                  letterSpacing: "-0.02em",
                  lineHeight: 1.2,
                }}
              >
                {displayText(order?.hotelName || booking?.hotelName)}
              </Typography>
              <Stack direction="row" flexWrap="wrap" alignItems="center" gap={1.5} sx={{ mt: 1.5 }}>
                <Stack direction="row" alignItems="center" spacing={0.5}>
                  <LocationOnIcon sx={{ fontSize: 18, color: tokens.accent }} />
                  <Typography variant="body1" fontWeight={600} color="text.secondary">
                    {formatBookingLocation(order)}
                  </Typography>
                </Stack>
                {String(order?.paymentStatus || "").toUpperCase() === "CHARGED" ? (
                  <Chip size="small" color="success" label="Paid" sx={{ fontWeight: 600 }} />
                ) : null}
              </Stack>
            </Box>
          </Stack>
        </Stack>

        <Divider sx={{ mb: 3, borderColor: tokens.border }} />

        <Grid container spacing={2.5}>
          <Grid item xs={12} md={6}>
            <SectionCard icon={<ReceiptLongIcon fontSize="small" />} title="Order">
              <DetailRow label="Order number" value={displayText(order?.orderNumber)} />
              <DetailRow label="Order date" value={formatDateTime(order?.orderDate)} />
              <DetailRow label="Order status" value={displayText(order?.orderStatus)} />
              <DetailRow label="Payment status" value={formatPaymentStatusDisplay(order?.paymentStatus)} />
              <DetailRow label="Payment mode" value={displayText(order?.paymentMode)} />
              <DetailRow label="Transaction ref" value={displayText(order?.transactionRef)} monospace />
              <DetailRow label="Payment date" value={formatDateTime(order?.paymentDate)} />
              <Divider sx={{ my: 1 }} />
              <DetailRow label="Total" value={formatMoney(order?.totalAmount, order?.currencyCode)} />
              <DetailRow label="Guests / rooms" value={`${order?.noOfGuests ?? "—"} guests · ${order?.noOfRooms ?? "—"} rooms`} />
              <DetailRow label="Check in date" value={formatDate(order?.checkInDate)} />
              <DetailRow label="Check out date" value={formatDate(order?.checkOutDate)} />
              {/* <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1.5, lineHeight: 1.5 }}>
                {displayText(order?.remarks)}
              </Typography> */}
            </SectionCard>
          </Grid>

          <Grid item xs={12} md={6}>
            <SectionCard icon={<HotelIcon fontSize="small" />} title="Booking Information">
              <DetailRow label="Booking ID" value={displayText(booking?.supplierBookingId)} monospace />
              <DetailRow label="Room type" value={displayText(booking?.roomType)} />
              <DetailRow label="Rate plan" value={displayText(booking?.ratePlan)} />
              <DetailRow label="Nights" value={booking?.numberOfNights != null ? String(booking.numberOfNights) : "—"} />
              <Divider sx={{ my: 1 }} />
              <DetailRow label="Base" value={formatMoney(booking?.baseAmount, booking?.currencyCode)} />
              <DetailRow label="Tax" value={formatMoney(booking?.taxAmount, booking?.currencyCode)} />
              <DetailRow label="Total" value={formatMoney(booking?.totalAmount, booking?.currencyCode)} />
              <DetailRow label="Booked on" value={formatDateTime(booking?.createdDate)} />
              {/* <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1.5 }}>
                Special request: {displayText(booking?.specialRequest)}
              </Typography> */}
            </SectionCard>
          </Grid>

        </Grid>

        <Typography
          variant="h6"
          sx={{ fontFamily: tokens.fontDisplay, mt: 4, mb: 1.5, color: tokens.brand }}
        >
          Guests
        </Typography>
        <TableContainer
          component={Paper}
          elevation={0}
          sx={{ border: `1px solid ${tokens.border}`, borderRadius: `${tokens.radiusMd}px`, mb: 3 }}
        >
          <Table size="small">
            <TableHead sx={{ bgcolor: tokens.surfaceAlt }}>
              <TableRow>
                <TableCell sx={{ fontWeight: 700 }}>Guest</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Type</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Contact</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>PAN</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Passport</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {(guests || []).length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} align="center">
                    No guest records
                  </TableCell>
                </TableRow>
              ) : (
                guests.map((g) => (
                  <TableRow key={g.guestId ?? g.email} hover>
                    <TableCell>
                      <Typography variant="body2" fontWeight={600}>
                        {displayText(g.fullName)}
                      </Typography>
                      {g.isLeadGuest ? (
                        <Chip label="Lead" size="small" sx={{ mt: 0.5, height: 20, fontSize: "0.65rem" }} />
                      ) : null}
                    </TableCell>
                    <TableCell>{displayText(g.guestType)}</TableCell>
                    <TableCell>
                      <Typography variant="body2">{displayText(g.email)}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {g.isdCode ? `+${g.isdCode} ` : ""}
                        {displayText(g.contactNumber)}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ fontFamily: "monospace", fontSize: "0.8rem" }}>{displayText(g.panCardNumber)}</TableCell>
                    <TableCell sx={{ fontFamily: "monospace", fontSize: "0.8rem" }}>
                      {displayText(g.passportNumber)}
                      {g.passportExpiry ? (
                        <Typography variant="caption" display="block" color="text.secondary">
                          exp {formatDate(g.passportExpiry)}
                        </Typography>
                      ) : null}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <Typography variant="h6" sx={{ fontFamily: tokens.fontDisplay, mt: 3, mb: 1.5, color: tokens.brand }}>
          Cancellation policy
        </Typography>
        <Paper
          elevation={0}
          sx={{
            p: 2,
            mb: 3,
            border: `1px solid ${tokens.border}`,
            borderRadius: `${tokens.radiusMd}px`,
            bgcolor: tokens.surfaceAlt,
          }}
        >
          {cancelRules.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              No structured cancellation rules returned for this booking.
            </Typography>
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 700 }}>From</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Until</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Charge</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {cancelRules.map((r, idx) => (
                    <TableRow key={idx}>
                      <TableCell>{formatDateTime(r.start)}</TableCell>
                      <TableCell>{formatDateTime(r.end)}</TableCell>
                      <TableCell>
                        {r.valueType === "Percentage" ? `${r.value}%` : formatMoney(r.value ?? r.estimatedValue, booking?.currencyCode)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Paper>

        <Box sx={{ display: "flex", justifyContent: "flex-end", pt: 1 }}>
          <Button variant="contained" onClick={onBack} sx={{ bgcolor: tokens.brand, "&:hover": { bgcolor: tokens.brandMuted } }}>
            Back to booking history
          </Button>
        </Box>
          </Paper>
        </Container>
      </Box>
    </>
  );
}

/** Legacy itinerary-based detail (?OrderId=) */
function LegacyBookingDetailsView({ bookingDetails }) {
  const navigate = useNavigate();
  const openCancellationPolicy = true;

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case "confirmed":
        return "success";
      case "cancelled":
        return "error";
      case "pending":
        return "warning";
      default:
        return "default";
    }
  };

  const handleDownloadReceipt = () => {
    toast.info("Downloading receipt...");
  };

  const handlePrintReceipt = () => {
    window.print();
  };

  const hotelInfo = bookingDetails.hotel_itinerary[0]?.staticContent[0];
  const roomInfo = bookingDetails.hotel_itinerary[0]?.items[0]?.selectedRoomsAndRates[0];
  const searchRequest = bookingDetails.hotel_itinerary[0]?.searchRequestLog;

  return (
    <>
      <ToastContainer position="top-right" autoClose={3000} />
      <Box sx={PAGE_SHELL_BOX_SX}>
        <Container maxWidth="xl" sx={PAGE_SHELL_CONTAINER_SX}>
          <Paper
            elevation={0}
            sx={{
              width: "100%",
              p: { xs: 2, md: 4 },
              borderRadius: `${tokens.radiusLg}px`,
              border: `1px solid ${tokens.border}`,
              boxShadow: "none",
              bgcolor: tokens.paper,
            }}
          >
        <Box sx={{ mb: 4 }}>
          <Stack
            direction={{ xs: "column", md: "row" }}
            justifyContent="space-between"
            alignItems={{ xs: "flex-start", md: "center" }}
            spacing={2}
          >
            <Box>
              <Typography variant="h4" sx={{ fontFamily: tokens.fontDisplay, color: tokens.brand }} fontWeight={700} gutterBottom>
                Booking Details
              </Typography>
            </Box>
            <Stack direction="row" spacing={2} flexWrap="wrap">
              <Chip label={bookingDetails.status} color={getStatusColor(bookingDetails.status)} sx={{ fontWeight: 600 }} />
              <Button variant="outlined" startIcon={<DownloadIcon />} onClick={handleDownloadReceipt}>
                Download Receipt
              </Button>
              <Button variant="outlined" startIcon={<PrintIcon />} onClick={handlePrintReceipt}>
                Print
              </Button>
            </Stack>
          </Stack>
        </Box>
        <Divider sx={{ mb: 2 }} />
        <Box sx={{ mb: 4 }}>
          <Stack direction="row" spacing={4} sx={{ flexWrap: "wrap", gap: 4 }}>
            <Box>
              <Typography variant="subtitle2" color="text.secondary">
                Order ID
              </Typography>
              <Typography variant="body1" fontWeight={500}>
                {bookingDetails.hotel_itinerary[0]?.orderId}
              </Typography>
            </Box>
            <Box>
              <Typography variant="subtitle2" color="text.secondary">
                Booking ID
              </Typography>
              <Typography variant="body1" fontWeight={500}>
                {bookingDetails.hotel_itinerary[0]?.bookingId}
              </Typography>
            </Box>
            <Box>
              <Typography variant="subtitle2" color="text.secondary">
                PNR Number
              </Typography>
              <Typography variant="body1" fontWeight={500}>
                {bookingDetails.hotel_itinerary[0]?.panNumber}
              </Typography>
            </Box>
          </Stack>
        </Box>

        <Divider sx={{ mb: 4 }} />

        <Grid container spacing={4}>
          <Grid item xs={12} md={6}>
            <Card elevation={0} sx={{ bgcolor: "background.default", height: "100%", border: `1px solid ${tokens.border}` }}>
              <CardContent>
                <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 3 }}>
                  <Avatar sx={{ bgcolor: tokens.accentSoft, color: tokens.accent }}>
                    <HotelIcon />
                  </Avatar>
                  <Typography variant="h6">Hotel Information</Typography>
                </Stack>
                <Stack spacing={2}>
                  <Box>
                    <Typography variant="subtitle2" color="text.secondary">
                      Hotel Name
                    </Typography>
                    <Typography variant="body1" fontWeight={500}>
                      {hotelInfo?.name}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="subtitle2" color="text.secondary">
                      Room Type
                    </Typography>
                    <Typography variant="body1" fontWeight={500}>
                      {roomInfo?.room?.name}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="subtitle2" color="text.secondary">
                      Address
                    </Typography>
                    <Typography variant="body1" fontWeight={500}>
                      {hotelInfo?.contact?.address?.line1}, {hotelInfo?.contact?.address?.city?.name},{" "}
                      {hotelInfo?.contact?.address?.state?.name}, {hotelInfo?.contact?.address?.country?.name}
                    </Typography>
                  </Box>
                </Stack>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={6}>
            <Card elevation={0} sx={{ bgcolor: "background.default", height: "100%", border: `1px solid ${tokens.border}` }}>
              <CardContent>
                <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 3 }}>
                  <Avatar sx={{ bgcolor: tokens.accentSoft, color: tokens.accent }}>
                    <CalendarIcon />
                  </Avatar>
                  <Typography variant="h6">Booking Dates</Typography>
                </Stack>
                <Stack spacing={2}>
                  <Box>
                    <Typography variant="subtitle2" color="text.secondary">
                      Check-in Date
                    </Typography>
                    <Typography variant="body1" fontWeight={500}>
                      {searchRequest?.checkIn ? dayjs(searchRequest.checkIn).format("DD MMM YYYY") : ""}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="subtitle2" color="text.secondary">
                      Check-out Date
                    </Typography>
                    <Typography variant="body1" fontWeight={500}>
                      {searchRequest?.checkOut ? dayjs(searchRequest.checkOut).format("DD MMM YYYY") : ""}
                    </Typography>
                  </Box>
                </Stack>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={6}>
            <Card elevation={0} sx={{ bgcolor: "background.default", height: "100%", border: `1px solid ${tokens.border}` }}>
              <CardContent>
                <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 3 }}>
                  <Avatar sx={{ bgcolor: tokens.accentSoft, color: tokens.accent }}>
                    <PaymentIcon />
                  </Avatar>
                  <Typography variant="h6">Payment Information</Typography>
                </Stack>
                <Stack spacing={2}>
                  <Box>
                    <Typography variant="subtitle2" color="text.secondary">
                      Total Amount
                    </Typography>
                    <Typography variant="h6" color="primary" fontWeight={700}>
                      ₹{bookingDetails.hotel_itinerary[0]?.totalAmount}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="subtitle2" color="text.secondary">
                      Cancellation Policy
                    </Typography>
                    <Typography variant="body1" fontWeight={500}>
                      {roomInfo?.rate?.isRefundable ? "Refundable" : "Non-Refundable"}
                    </Typography>
                  </Box>
                </Stack>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={6}>
            <Card elevation={0} sx={{ bgcolor: "background.default", height: "100%", border: `1px solid ${tokens.border}` }}>
              <CardContent>
                <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 3 }}>
                  <Avatar sx={{ bgcolor: tokens.accentSoft, color: tokens.accent }}>
                    <PersonIcon />
                  </Avatar>
                  <Typography variant="h6">Guest Information</Typography>
                </Stack>
                <Stack spacing={2}>
                  {bookingDetails.hotel_itinerary[0]?.items[0]?.selectedRoomsAndRates?.map((room, roomIndex) => (
                    <Box key={roomIndex} sx={{ mb: 2 }}>
                      <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1 }}>
                        Room {roomIndex + 1}: {room.room?.name}
                      </Typography>
                      <Box sx={{ pl: 2 }}>
                        {room.room?.guests?.map((guest, guestIndex) => (
                          <Box key={guestIndex} sx={{ mb: 2 }}>
                            <Typography variant="subtitle2" color="text.secondary">
                              Guest {guestIndex + 1}
                            </Typography>
                            <Typography variant="body1" fontWeight={500}>
                              {guest.title} {guest.firstName} {guest.middleName} {guest.lastName}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              Type: {guest.type} {guest.isLeadGuest ? "(Lead Guest)" : ""}
                            </Typography>
                          </Box>
                        ))}
                      </Box>
                    </Box>
                  ))}
                </Stack>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        <Box sx={{ mt: 4, p: 2, bgcolor: tokens.accentSoft, borderRadius: 2 }}>
          <Typography variant="h6">Cancellation Policy</Typography>
          <Collapse in={openCancellationPolicy}>
            {bookingDetails?.hotel_itinerary[0]?.items[0]?.selectedRoomsAndRates[0]?.rate?.cancellationPolicies?.[0]?.rules ? (
              <TableContainer component={Paper} sx={{ mt: 2, boxShadow: "none" }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 600 }}>Start Date</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>End Date</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Charges</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {bookingDetails.hotel_itinerary[0].items[0].selectedRoomsAndRates[0].rate.cancellationPolicies[0].rules.map(
                      (policy, index) => (
                        <TableRow key={index}>
                          <TableCell>{dayjs(policy.start).format("DD MMM YYYY")}</TableCell>
                          <TableCell>{dayjs(policy.end).format("DD MMM YYYY")}</TableCell>
                          <TableCell>{policy.valueType === "Percentage" ? `${policy.value}%` : `₹${policy.value}`}</TableCell>
                        </TableRow>
                      )
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : (
              <Box sx={{ mt: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  No cancellation policy available
                </Typography>
              </Box>
            )}
          </Collapse>
        </Box>

        <Box sx={{ mt: 4, display: "flex", justifyContent: "flex-end", gap: 2 }}>
          <Button variant="outlined" onClick={() => navigate("/transaction-history")}>
            Back to List
          </Button>
        </Box>
          </Paper>
        </Container>
      </Box>
    </>
  );
}

export default function BookingDetails() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const bookingId = searchParams.get("bookingId");
  const supplierBookingId = searchParams.get("supplierBookingId");
  const clientId = searchParams.get("clientId");
  const userId = searchParams.get("userId");
  const legacyOrderId = searchParams.get("OrderId");

  const [loading, setLoading] = useState(true);
  const [completeData, setCompleteData] = useState(null);
  const [legacyData, setLegacyData] = useState(null);
  const [loadError, setLoadError] = useState(null);

  const hasCompleteParams =
    bookingId &&
    supplierBookingId &&
    clientId != null &&
    clientId !== "" &&
    userId != null &&
    userId !== "";

  useEffect(() => {
    let cancelled = false;

    const run = async () => {
      setLoadError(null);
      setCompleteData(null);
      setLegacyData(null);

      if (hasCompleteParams) {
        const payload = {
          bookingId: parseInt(bookingId, 10),
          supplierBookingId: String(supplierBookingId),
          clientId: parseInt(clientId, 10),
          userId: parseInt(userId, 10),
        };
        if ([payload.bookingId, payload.clientId, payload.userId].some((n) => Number.isNaN(n))) {
          toast.error("Invalid booking parameters.");
          setLoading(false);
          setLoadError("Invalid booking parameters.");
          return;
        }
        try {
          setLoading(true);
          const response = await searchAPI.getBookingCompleteDetails(payload);
          const ok =
            response?.status === "success" && Number(response?.statusCode) === 200 && response?.data;
          if (cancelled) return;
          if (ok) {
            setCompleteData(response.data);
          } else {
            const msg = response?.message || "Could not load booking details.";
            toast.error(msg);
            setLoadError(msg);
          }
        } catch (error) {
          if (cancelled) return;
          const msg = error?.response?.data?.message || error?.message || "Failed to load booking details.";
          toast.error(msg);
          setLoadError(msg);
          console.error(error);
        } finally {
          if (!cancelled) setLoading(false);
        }
        return;
      }

      if (legacyOrderId) {
        try {
          setLoading(true);
          const response = await searchAPI.getBookingDetails(legacyOrderId);
          if (cancelled) return;
          if (response.status) {
            setLegacyData(response.data);
          } else {
            toast.error(response.message || "Failed to fetch booking details");
            setLoadError(response.message || "Failed to fetch booking details");
          }
        } catch (error) {
          if (cancelled) return;
          toast.error("Failed to fetch booking details");
          setLoadError("Failed to fetch booking details");
          console.error(error);
        } finally {
          if (!cancelled) setLoading(false);
        }
        return;
      }

      setLoading(false);
      setLoadError("Missing booking reference.");
      toast.error("No booking reference provided.");
    };

    run();
    return () => {
      cancelled = true;
    };
  }, [hasCompleteParams, bookingId, supplierBookingId, clientId, userId, legacyOrderId]);

  const handleBack = () => navigate("/transaction-history");

  if (loading) {
    return <Loader />;
  }

  if (completeData) {
    return <CompleteBookingDetailsView data={completeData} onBack={handleBack} />;
  }

  if (legacyData) {
    return <LegacyBookingDetailsView bookingDetails={legacyData} />;
  }

  return (
    <>
      <ToastContainer position="top-right" autoClose={3000} />
      <Box sx={PAGE_SHELL_BOX_SX}>
        <Container maxWidth="xl" sx={PAGE_SHELL_CONTAINER_SX}>
          <Box sx={{ py: 6, px: 2, textAlign: "center", maxWidth: 480, mx: "auto" }}>
            <Typography variant="h6" gutterBottom sx={{ fontFamily: tokens.fontDisplay, color: tokens.brand }}>
              Booking not found
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {loadError || "Open a booking from your history, or use a valid link."}
            </Typography>
            <Button variant="contained" onClick={handleBack} sx={{ bgcolor: tokens.brand }}>
              Back to booking history
            </Button>
          </Box>
        </Container>
      </Box>
    </>
  );
}
